import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import { useDeleteSchedule, useSchedule, useSends, useUpdateSchedule } from 'hooks/api'
import { CheckCircle, CircleAlert, Paperclip, Pause, Pencil, Play, Save, Trash2, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { RRule } from 'rrule'
import type { Attachment } from 'types/attachments'
import type { ScheduleDetail as ScheduleDetailType } from 'types/schedules'

import { type Recipient } from '../communications/recipient-selector'
import { type ScheduleFormData, ScheduleFormFields } from './schedule-form-fields'
function rruleToText(rruleStr: string): string {
  try {
    return RRule.fromString(rruleStr).toText()
  } catch {
    return rruleStr
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

function PauseButton({
  onPauseIndefinitely,
  onPauseUntil,
  disabled,
}: {
  onPauseIndefinitely: () => void
  onPauseUntil: (until: string) => void
  disabled?: boolean
}) {
  const [pauseDate, setPauseDate] = useState('')
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Pause className="mr-1 h-4 w-4" />
          Pause
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">Pause schedule</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              onPauseIndefinitely()
              setOpen(false)
            }}>
            Pause indefinitely
          </Button>
          <Separator />
          <div className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">Pause until</span>
            <Input type="datetime-local" value={pauseDate} onChange={(e) => setPauseDate(e.target.value)} />
            <Button
              size="sm"
              disabled={!pauseDate}
              onClick={() => {
                onPauseUntil(pauseDate)
                setOpen(false)
                setPauseDate('')
              }}>
              Pause until date
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function contactDisplayName(contact: {
  firstName: string | null
  lastName: string | null
  email: string | null
}): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || '(unnamed)'
}

function SendStatusIcon({ status }: { status: string }) {
  if (status === 'SENT') {
    return <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500" />
  }
  if (status === 'PARTIAL') {
    return <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-500" />
  }
  if (status === 'FAILED') {
    return <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
  }
  return <div className="mt-0.5 h-3.5 w-3.5 shrink-0" />
}

/** Build edit form state from the loaded schedule detail. */
function scheduleToFormData(schedule: ScheduleDetailType): {
  formData: ScheduleFormData
  recipients: Recipient[]
  attachments: Attachment[]
} {
  const sc = schedule.scheduledCommunication
  return {
    formData: {
      name: sc.name,
      method: sc.method,
      subject: sc.subject ?? '',
      templateId: sc.templateId ?? '',
      eventData: sc.eventData ? JSON.stringify(sc.eventData, null, 2) : '',
      rrule: schedule.rrule,
    },
    recipients: [
      ...sc.contacts.map((c) => ({
        type: 'contact' as const,
        id: c.contact.id,
        label: contactDisplayName(c.contact),
      })),
      ...sc.contactGroups.map((cg) => ({
        type: 'group' as const,
        id: cg.contactGroup.id,
        label: cg.contactGroup.name,
      })),
    ],
    attachments: sc.attachments.map((a) => ({
      id: a.attachment.id,
      filename: a.attachment.filename,
      contentType: a.attachment.contentType,
      fileSize: a.attachment.fileSize,
      storageKey: '',
      active: true,
      createdAt: '',
      updatedAt: '',
    })),
  }
}

/* ── Read-only view ── */

function ScheduleReadView({ schedule }: { schedule: ScheduleDetailType }) {
  const sc = schedule.scheduledCommunication
  const hasEventData = sc.eventData && Object.keys(sc.eventData).length > 0

  const isPaused = schedule.pausedUntil && new Date(schedule.pausedUntil).getTime() > Date.now()
  const isIndefinitelyPaused = isPaused && new Date(schedule.pausedUntil!).getFullYear() >= 9999

  return (
    <dl className="flex flex-col gap-4">
      <InfoRow label="Delivery Method">
        <Badge variant="secondary" className="text-xs">
          {sc.method}
        </Badge>
      </InfoRow>

      {sc.template && (
        <InfoRow label="Template">
          <Link to={`/templates/${sc.template.id}`} className="text-foreground hover:underline">
            {sc.template.name}
          </Link>
        </InfoRow>
      )}

      {sc.subject && <InfoRow label="Subject">{sc.subject}</InfoRow>}

      {(sc.contacts.length > 0 || sc.contactGroups.length > 0) && (
        <InfoRow label="Recipients">
          <div className="mt-1 flex flex-wrap gap-1.5">
            {sc.contactGroups.map((cg) => (
              <Badge key={cg.id} variant="secondary">
                Group: {cg.contactGroup.name}
              </Badge>
            ))}
            {sc.contacts.map((c) => (
              <Badge key={c.id} variant="outline">
                {contactDisplayName(c.contact)}
              </Badge>
            ))}
          </div>
        </InfoRow>
      )}

      {hasEventData && (
        <InfoRow label="Event Data">
          <pre className="mt-1 max-h-32 overflow-auto rounded bg-muted p-2 font-mono text-xs">
            {JSON.stringify(sc.eventData, null, 2)}
          </pre>
        </InfoRow>
      )}

      {sc.attachments.length > 0 && (
        <InfoRow label="Attachments">
          <div className="mt-1 flex flex-wrap gap-1.5">
            {sc.attachments.map((a) => (
              <Badge key={a.attachment.id} variant="outline" className="gap-1">
                <Paperclip className="h-3 w-3" />
                {a.attachment.filename}
                <span className="text-muted-foreground">({formatFileSize(a.attachment.fileSize)})</span>
              </Badge>
            ))}
          </div>
        </InfoRow>
      )}

      <Separator />

      <InfoRow label="Recurrence">
        <span className="capitalize">{rruleToText(schedule.rrule)}</span>
      </InfoRow>
      {schedule.lastRun && <InfoRow label="Last Run">{new Date(schedule.lastRun).toLocaleString()}</InfoRow>}
      {isPaused && (
        <InfoRow label="Paused Until">
          {isIndefinitelyPaused ? (
            <span className="italic">Indefinitely</span>
          ) : (
            <span>
              {new Date(schedule.pausedUntil!).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
        </InfoRow>
      )}
      {schedule.nextExecutionTime && !isPaused && (
        <InfoRow label="Next Run">{new Date(schedule.nextExecutionTime).toLocaleString()}</InfoRow>
      )}
    </dl>
  )
}

/* ── Main detail content ── */

function ScheduleDetailContent({ schedule, refetch }: { schedule: ScheduleDetailType | null; refetch: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { perform: updateSchedule, loading: saving } = useUpdateSchedule()
  const { perform: deleteSchedule } = useDeleteSchedule()
  const { data: recentSends } = useSends(id ? { scheduleId: id, take: 10 } : undefined)

  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState<ScheduleFormData | null>(null)
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [attachments, setAttachments] = useState<Attachment[]>([])

  const startEditing = useCallback(() => {
    if (schedule) {
      const state = scheduleToFormData(schedule)
      setFormData(state.formData)
      setRecipients(state.recipients)
      setAttachments(state.attachments)
    }
    setEditing(true)
  }, [schedule])

  const cancelEditing = useCallback(() => {
    setEditing(false)
    setFormData(null)
  }, [])

  const handleFieldChange = useCallback((field: keyof ScheduleFormData, value: string) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev))
  }, [])

  const handleAddRecipient = useCallback((recipient: Recipient) => {
    setRecipients((prev) => [...prev, recipient])
  }, [])

  const handleRemoveRecipient = useCallback((recipient: Recipient) => {
    setRecipients((prev) => prev.filter((r) => !(r.type === recipient.type && r.id === recipient.id)))
  }, [])

  const handleSave = useCallback(async () => {
    if (!id || !formData) {
      return
    }

    let eventData: Record<string, unknown> | null = null
    if (formData.eventData.trim()) {
      try {
        eventData = JSON.parse(formData.eventData)
      } catch {
        return // don't save with invalid JSON
      }
    }

    await updateSchedule(id, {
      rrule: formData.rrule,
      name: formData.name,
      method: formData.method,
      subject: formData.method === 'EMAIL' ? formData.subject || null : null,
      templateId: formData.templateId || null,
      eventData,
      contactIds: recipients.filter((r) => r.type === 'contact').map((r) => r.id),
      contactGroupIds: recipients.filter((r) => r.type === 'group').map((r) => r.id),
      attachmentIds: attachments.map((a) => a.id),
    })
    refetch()
    setEditing(false)
    setFormData(null)
  }, [id, formData, recipients, attachments, updateSchedule, refetch])

  const handlePauseIndefinitely = useCallback(async () => {
    if (!id) {
      return
    }
    await updateSchedule(id, { pausedUntil: '9999-12-31T23:59:59.000Z' })
    refetch()
  }, [id, updateSchedule, refetch])

  const handlePauseUntil = useCallback(
    async (until: string) => {
      if (!id || !until) {
        return
      }
      await updateSchedule(id, { pausedUntil: new Date(until).toISOString() })
      refetch()
    },
    [id, updateSchedule, refetch],
  )

  const handleResume = useCallback(async () => {
    if (!id) {
      return
    }
    await updateSchedule(id, { pausedUntil: null })
    refetch()
  }, [id, updateSchedule, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    await deleteSchedule(id)
    navigate('/schedule')
  }, [id, deleteSchedule, navigate])

  const nextDates = useMemo(() => {
    if (!schedule) {
      return []
    }
    try {
      const rule = RRule.fromString(schedule.rrule)
      return rule.all((_, i) => i < 5)
    } catch {
      return []
    }
  }, [schedule])

  if (!schedule?.scheduledCommunication) {
    return <div className="py-12 text-center text-muted-foreground">Schedule not found</div>
  }

  const sc = schedule.scheduledCommunication
  const isPaused = schedule.pausedUntil && new Date(schedule.pausedUntil).getTime() > Date.now()
  const isIndefinitelyPaused = isPaused && new Date(schedule.pausedUntil!).getFullYear() >= 9999

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <Card className="min-w-0 flex-1">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <CardTitle>{sc.name}</CardTitle>
            {isPaused ? (
              <Badge variant="outline" className="text-xs">
                {isIndefinitelyPaused
                  ? 'Paused'
                  : `Paused until ${new Date(schedule.pausedUntil!).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
              </Badge>
            ) : (
              <Badge variant={schedule.active ? 'default' : 'secondary'} className="text-xs">
                {schedule.active ? 'Active' : 'Inactive'}
              </Badge>
            )}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="mr-1 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                {schedule.active && (
                  <>
                    {isPaused ? (
                      <Button variant="outline" size="sm" onClick={handleResume} disabled={saving}>
                        <Play className="mr-1 h-4 w-4" />
                        Resume
                      </Button>
                    ) : (
                      <PauseButton
                        onPauseIndefinitely={handlePauseIndefinitely}
                        onPauseUntil={handlePauseUntil}
                        disabled={saving}
                      />
                    )}
                  </>
                )}
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Button>
                <ConfirmDialog
                  title="Delete schedule"
                  description={`Are you sure you want to delete the schedule for "${sc.name}"? This action cannot be undone.`}
                  onConfirm={handleDelete}
                  trigger={
                    <Button variant="destructive" size="sm">
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </Button>
                  }
                />
              </>
            )}
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="pt-6">
          {editing && formData ? (
            <ScheduleFormFields
              data={formData}
              onChange={handleFieldChange}
              recipients={recipients}
              onAddRecipient={handleAddRecipient}
              onRemoveRecipient={handleRemoveRecipient}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
            />
          ) : (
            <ScheduleReadView schedule={schedule} />
          )}
        </CardContent>
      </Card>

      {/* Sidebar panels — read mode only */}
      {!editing && (
        <div className="flex min-w-0 flex-col gap-4 lg:w-72">
          {nextDates.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Upcoming Runs</CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="pt-4">
                <div className="flex flex-col gap-1.5">
                  {nextDates.map((d, i) => (
                    <p key={i} className="text-sm text-foreground">
                      {d.toLocaleString()}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Recent Runs</CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-4">
              {!recentSends || recentSends.length === 0 ? (
                <p className="text-sm text-muted-foreground">No runs yet.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {recentSends.map((send) => (
                    <Link
                      key={send.id}
                      to={`/sends/${send.id}`}
                      className="flex items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted/50">
                      <SendStatusIcon status={send.status} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">
                          {new Date(send.startedAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                        <div className="flex items-center gap-2 text-xs">
                          <span>{send.recipientCount} recipients</span>
                          {send.failureCount > 0 && (
                            <span className="text-destructive">{send.failureCount} failed</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export function ScheduleDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: schedule, loading, refetch } = useSchedule(id)

  if (!loading && !schedule) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">Schedule not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/schedule">Back to Schedules</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>
        }>
        <ScheduleDetailContent schedule={schedule} refetch={refetch} />
      </LoadingWrapper>
    </Page>
  )
}
